/**
 * TIP Shell — THE TEST RUN. Framework-agnostic; the console drives it.
 *
 * WHY THIS EXISTS. The six governed ship gates (doc 06) decide what an app MAY DO. Not
 * one of them can decide whether it WORKS: an activity that throws on its first line is
 * perfectly safe and passes all six. So before a teacher may publish, they run it — and
 * this module is what running it means.
 *
 * WHERE IT RUNS: the REAL sandbox origin, the REAL sealed opaque-origin iframe, the REAL
 * CSP, the REAL SDK, the REAL port handshake. A jsdom harness on the server would have
 * been easier and would have proved nothing — it would be a different environment, so a
 * pass there would not mean a pass here. The only difference from a student's launch is
 * WHO IS ON THE OTHER END OF THE PORT (see below).
 *
 * WHAT IT IS NOT ALLOWED TO TOUCH — the reason the stand-in below exists at all:
 *   • No real student. `tip:init` carries invented tags and one invented first name.
 *   • No real roster. There is no class here; the leaderboard is three made-up rows.
 *   • No score is ever written. `writeScore` resolves locally and stores nothing —
 *     the draft has no app id and no assignment, so there is nothing it COULD write to,
 *     and the stand-in makes that structural rather than incidental.
 *   • No network from this module at all, beyond the frame fetching its own source.
 *
 * WHAT THE VERDICT IS WORTH: it is what the runtime reported, and no more. It is shown
 * to the teacher and recorded as evidence that a human ran these exact bytes. It cannot
 * relax a gate — every safety decision was made server-side before this ran.
 */
import type { Tier, FrameRuntimeReport } from './protocol.js';
import type { BridgeApi } from './bridge.js';
import { Bridge } from './bridge.js';
import { createSealedFrame } from './seal.js';
import { NameOverlay } from './name-overlay.js';
import type { TestRunStatus } from './api.js';

/** Bound cold frame/SDK/app-source loading separately from the render settle time. */
const LOAD_TIMEOUT_MS = 20_000;
/** How long the app gets to boot and paint AFTER its scripts load. */
const SETTLE_MS = 2_500;
/** How long the frame has to answer the probe. */
const PROBE_TIMEOUT_MS = 3_000;

export interface TestRunVerdict {
  artifactSha256?: string;
  status: TestRunStatus;
  /** One sentence for the teacher — the console shows this verbatim. */
  headline: string;
  /** The runtime's own words, bounded. Empty when it ran clean. */
  errors: string[];
  /** What we saw, for the record and for the refine loop's context. */
  report: FrameRuntimeReport | null;
}

export interface TestRunHandle {
  /** Resolves once the app has been given time to boot and has been probed. */
  verdict: Promise<TestRunVerdict>;
  /** Tear the frame down. Safe to call more than once. */
  stop(): void;
}

/**
 * The stand-in on the other end of the port.
 *
 * Every method answers locally. This is the whole privacy story of the test run: a
 * teacher trying out a draft cannot reach a roster, cannot resolve a name, and cannot
 * write a score, because the thing that would do those jobs is not connected.
 */
function standInApi(log: (m: string) => void): BridgeApi {
  const NAMES: Record<string, string> = {
    t_demo_self: 'Sam',
    t_demo_1: 'Alex',
    t_demo_2: 'Jordan',
    t_demo_3: 'Riley',
  };
  return {
    resolveIdentity: (tags: string[]) =>
      Promise.resolve({
        resolved: tags.map((tag) => (NAMES[tag] ? { tag, given: NAMES[tag] } : { tag })),
      }),
    resolveLeaderboard: () =>
      Promise.resolve({
        deny: false,
        rows: [
          { tag: 't_demo_1', given: 'Alex', score: 9, max_score: 10 },
          { tag: 't_demo_self', given: 'Sam', score: 7, max_score: 10 },
          { tag: 't_demo_2', given: 'Jordan', score: 5, max_score: 10 },
        ],
      }),
    broker: (_reqId: string, method: string) => {
      if (method === 'getMyToken') {
        return Promise.resolve({ ok: true, data: { token: 't_demo_self' } });
      }
      if (method === 'writeScore') {
        // Deliberately a no-op that REPORTS SUCCESS: the app must take its normal
        // "score saved" path so the teacher sees the activity end the way a student
        // would. Nothing is stored — there is no assignment to store it against.
        log('the app saved a score — discarded (this is a test run)');
        return Promise.resolve({ ok: true, data: { status: 'recorded' } });
      }
      if (method === 'getScores') {
        return Promise.resolve({
          ok: true,
          data: [
            { score: 9, max_score: 10, student_tag: 't_demo_1' },
            { score: 7, max_score: 10, student_tag: 't_demo_self' },
          ],
        });
      }
      return Promise.resolve({ ok: false, error: `unknown method: ${method}` });
    },
    // There is no published app here, so there is nothing to quarantine. A self-nav in a
    // draft is still surfaced to the teacher through onBreach — it just has no tenant-wide
    // consequence, because no student can reach this.
    adminKill: () => {
      log('the app tried to navigate the page — that is blocked, and it would be blocked for students too');
      return Promise.resolve(false);
    },
  };
}

/** Turn what the frame reported into the one sentence a teacher should read. */
export function classify(report: FrameRuntimeReport | null): TestRunVerdict {
  if (!report) {
    return {
      status: 'unreachable',
      headline: 'The preview did not respond in time. Run it again to check it.',
      errors: [],
      report: null,
    };
  }
  if (report.errors.length > 0) {
    return {
      status: 'ran_with_errors',
      headline:
        report.rendered > 0
          ? `It ran, but hit ${report.errors.length} ${report.errors.length === 1 ? 'error' : 'errors'}. Parts of it may not work.`
          : `It failed to start: ${report.errors.length} ${report.errors.length === 1 ? 'error' : 'errors'} and nothing on screen.`,
      errors: report.errors,
      report,
    };
  }
  if (report.rendered === 0) {
    return {
      status: 'blank',
      headline: report.ready
        ? 'It started without errors but put nothing on screen.'
        : 'It never finished starting, and put nothing on screen.',
      errors: [],
      report,
    };
  }
  return {
    status: 'ran_clean',
    headline: report.wroteScore
      ? 'It ran, showed the activity, and saved a score. No errors.'
      : 'It ran and showed the activity. No errors.',
    errors: [],
    report,
  };
}

export interface TestRunInput {
  kind?: 'assignment'|'resource' | undefined;
  /** Where to put the frame. The caller owns this element. */
  container: HTMLElement;
  sandboxOrigin: string;
  /** The BUILD id — a draft is addressed by build, not by app (it is not an app yet). */
  buildId: string;
  /** The short-lived draft credential from POST /teacher/build/:id/test-run. */
  draftToken: string;
  /** The tier this build was written for — drives the sandbox's CSP exactly as at launch. */
  tier: Tier;
  /** Progress/diagnostic lines for the console. */
  log?: ((message: string) => void) | undefined;
  /** Override the settle delay (tests). */
  settleMs?: number | undefined;
  /** Override the frame loading deadline (tests). */
  loadTimeoutMs?: number | undefined;
  /** The teacher's school look, so the test run looks like the launch will. */
  theme?: { schoolName: string; mascot: string; primary: string; accent: string } | undefined;
}

/**
 * Mount the draft in a sealed frame, let it run, then ask it how it went.
 *
 * The caller gets the handle synchronously so it can render the frame immediately and
 * show the verdict when it lands — a teacher watches the activity boot, which is most of
 * the value; the verdict is the part they can act on.
 */
export function startTestRun(input: TestRunInput): TestRunHandle {
  const log = input.log ?? (() => void 0);
  const nonce = crypto.randomUUID();

  // A draft app is a GENERATED app: the sandbox resolves it by uuid and fetches its
  // source with the token. The only difference from a published launch is which domain
  // signed the token — and the server, not the sandbox, is what tells them apart.
  const iframe = createSealedFrame({
    sandboxOrigin: input.sandboxOrigin,
    framePath: '/frame.html',
    tier: input.tier,
    nonce,
    appId: input.buildId,
    appSourceToken: input.draftToken,
  });

  // The student's name goes OVER the app's own anchor ("Hi, ⎽⎽⎽⎽."), in a parent-DOM layer
  // laid over the frame — the same mechanism as a student's launch (bootstrap.tsx), so
  // what the teacher approves is what a student sees. The strip below the frame is shown
  // for a slot exactly while no chip is painted for it (no geometry yet, the anchor left,
  // or it scrolled out of the frame), so the name is never shown NOWHERE — a teacher must
  // not approve an app whose greeting went blank without seeing that it did.
  const SELF_TAG = 't_demo_self';
  const slotNames = new Map<string, string>();
  const overlay = new NameOverlay({
    iframe,
    onPaintedChange: (slotId, painted) => {
      if (painted) removeStrip(input.container, slotId);
      else {
        const name = slotNames.get(slotId);
        if (name != null) paintInto(input.container, slotId, name);
      }
    },
  });

  const bridge = new Bridge({
    iframe,
    sandboxOrigin: input.sandboxOrigin,
    expectedNonce: nonce,
    tier: input.tier,
    // A test run shows the stand-in first name at standard tier, exactly as a student's
    // launch would — so a teacher sees the greeting and can tell whether it reads right.
    // At high_assurance the frame stays name-blind, also exactly as it would.
    mayExposeFirstName: input.tier === 'standard',
    appId: input.buildId,
    self: { tag: SELF_TAG },
    roster: [{ tag: 't_demo_1' }, { tag: 't_demo_2' }, { tag: 't_demo_3' }],
    context: input.kind === 'resource' ? {kind:'resource',classId:'demo-class',resourceId:'demo-resource',artifactId:'demo-artifact',revision:1} : { classId: 'demo-class', assignmentId: 'demo-assignment' },
    ...(input.theme ? { theme: input.theme } : {}),
    api: standInApi(log),
    host: {
      log: (_kind, m) => log(m),
      // Identity slots and the leaderboard paint into the PARENT DOM here too — the
      // same trusted-chrome mechanism, so what the teacher sees is what students see.
      paintSlot: (slotId, tag, name) => {
        slotNames.set(slotId, name);
        // Only the viewer's OWN name is ever positioned in the app (the Bridge enforces
        // this too; the host does not rely on it).
        if (tag === SELF_TAG) overlay.setName(slotId, name);
        if (!overlay.isPainted(slotId)) paintInto(input.container, slotId, name);
      },
      placeSlot: (slotId, rect) => overlay.setRect(slotId, rect),
      paintLeaderboard: (slotId, rows) =>
        paintInto(
          input.container, slotId,
          rows.map((r, i) => `${i + 1}. ${r.given ?? r.tag} — ${r.score}/${r.max_score}`).join('  ·  '),
        ),
      setStatus: (m) => log(m),
      onBreach: (reason) => log(`stopped by the platform: ${reason}`),
    },
  });

  let stopped = false;
  let finished = false;
  let loadTimer: number | undefined;
  let settleTimer: number | undefined;
  let resolveVerdict: (value: TestRunVerdict) => void;
  const verdict = new Promise<TestRunVerdict>((resolve) => { resolveVerdict = resolve; });
  const finish = (report: FrameRuntimeReport | null): void => {
    if (finished) return;
    finished = true;
    window.clearTimeout(loadTimer);
    window.clearTimeout(settleTimer);
    iframe.removeEventListener('load', onLoad);
    resolveVerdict(classify(report));
  };
  const onLoad = (): void => {
    if (stopped || finished) return;
    window.clearTimeout(loadTimer);
    // The SDK precedes a blocking external app script. A port can bind while that
    // source is still in flight, so neither append time nor handshake time means
    // the app has had a chance to paint. Load only starts the settle window; a
    // clean verdict STILL requires the existing authenticated port probe/report.
    settleTimer = window.setTimeout(() => {
      if (stopped || finished) return;
      void bridge.probe(PROBE_TIMEOUT_MS).then(finish, () => finish(null));
    }, input.settleMs ?? SETTLE_MS);
  };
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    finish(null);
    bridge.dispose();
    overlay.dispose();
    iframe.remove();
  };

  // Arm before append: even a warm cached frame must not race its load listener.
  iframe.addEventListener('load', onLoad, { once: true });
  loadTimer = window.setTimeout(() => finish(null), input.loadTimeoutMs ?? LOAD_TIMEOUT_MS);
  bridge.start();
  input.container.appendChild(iframe);

  return { verdict, stop };
}

/**
 * Paint a trusted-chrome slot in the PARENT DOM — the same rule as a live launch: a name
 * the platform resolved is written as TEXT into a parent-DOM element and never returned
 * to the frame. Created on demand, because a draft's slot ids are whatever the model
 * chose and the console cannot have rendered them in advance.
 */
function stripFor(container: HTMLElement, slotId: string): HTMLElement | null {
  const id = `tip-slot-${slotId}`;
  return container.querySelector<HTMLElement>(`.testrun-slot[data-tip-slot="${CSS.escape(id)}"]`);
}

/** The anchor has geometry now, so the name is painted over it — the strip is redundant. */
function removeStrip(container: HTMLElement, slotId: string): void {
  stripFor(container, slotId)?.remove();
}

function paintInto(container: HTMLElement, slotId: string, text: string): void {
  const id = `tip-slot-${slotId}`;
  let el = stripFor(container, slotId);
  if (!el) {
    el = document.createElement('div');
    el.setAttribute('data-tip-slot', id);
    el.className = 'testrun-slot';
    container.appendChild(el);
  }
  el.textContent = text; // textContent, never innerHTML — this string came from the frame
}
